import { useMemo, useState } from 'react'

import { Box, Typography, CircularProgress } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import APIs from '../../../api'
import logoSm from '../../../assets/logo-sm.png'
import { ROUTES } from '../../../constants'
import { useLanguage } from '../../../i18n/LanguageContext'
import { globalSettingsStorage } from '../../../utils/globalSettingsStorage'
import { getAllowedPlatformSlugs } from '../../../utils/loginMeta'
import { remoteLogin } from '../../../utils/remoteLogin'
import { tokenStorage } from '../../../utils/tokenStorage'
import { userStorage } from '../../../utils/userStorage'
import { getGlobalSettings } from '../../globalSettings/api'

// Live `getGuardianMeApi` is enabled. Static payload kept as a fallback for
// the Guardian flow while `/guardian-users/me` is offline.
// import STATIC_ME_RESPONSE from './staticMeResponse'
import { getGuardianMeApi, listProductsApi } from './api'

// Omnia is a planned-but-not-yet-released product, so it's rendered from a
// static "Coming soon" card rather than from the `/products` API response.
// Every other card comes from the live list.
const STATIC_OMNIA = {
  id: 'p-omnia',
  name: 'Omnia',
  enabled: false,
  type: 'omnia',
}

// Map an API product slug (e.g. "MDG_MOVE", "MDG_GUARDIAN") to the internal
// handler key. Falls back to `other` so unknown products just bounce to the
// branch-selection screen instead of crashing.
function getProductType(slug) {
  const upper = (slug || '').toUpperCase()
  if (upper === 'MDG_MOVE') return 'move'
  if (upper === 'MDG_GUARDIAN') return 'guardian'
  return 'other'
}

// Decorative concentric arcs sitting in the page corners.
function BackdropArc({ sx }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        pointerEvents: 'none',
        ...sx,
      }}
    >
      <svg viewBox="0 0 600 600" width="100%" height="100%">
        <circle
          cx="300"
          cy="300"
          r="260"
          fill="none"
          stroke="rgba(95,125,255,0.35)"
          strokeWidth="1"
        />
        <circle
          cx="300"
          cy="300"
          r="180"
          fill="none"
          stroke="rgba(95,125,255,0.25)"
          strokeWidth="1"
        />
        <circle
          cx="300"
          cy="300"
          r="100"
          fill="none"
          stroke="rgba(95,125,255,0.2)"
          strokeWidth="1"
        />
      </svg>
    </Box>
  )
}

function ProductLogo({ size = 56 }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        component="img"
        src={logoSm}
        alt="MDG logo"
        sx={{ width: size, height: size, objectFit: 'contain' }}
      />
    </Box>
  )
}

function ProductCard({ product, loading, onSelect }) {
  const enabled = product.enabled
  // Distinguish "not released yet" (status=INACTIVE) from "this user isn't
  // licensed for it" (status=ACTIVE but slug missing from allowedPlatform).
  // The former gets the existing "Coming soon" caption; the latter gets a
  // clearer "Not available for your account" so users don't think the
  // platform is unlaunched.
  const isNotAllowed = product.disabledReason === 'notAllowed'
  const disabledCaption = isNotAllowed ? 'Not available for your account' : 'Coming soon'
  return (
    <Box
      onClick={() => enabled && !loading && onSelect(product)}
      role="button"
      tabIndex={enabled ? 0 : -1}
      aria-disabled={!enabled}
      onKeyDown={(e) => enabled && (e.key === 'Enter' || e.key === ' ') && onSelect(product)}
      sx={{
        width: 160,
        height: 150,
        borderRadius: 3,
        bgcolor: 'var(--card-bg)',
        border: '1px solid var(--divider-color)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        cursor: enabled ? 'pointer' : 'not-allowed',
        opacity: enabled ? 1 : 0.45,
        transition: 'all 0.15s ease',
        '&:hover': enabled
          ? {
              bgcolor: 'var(--subcard-bg)',
              borderColor: 'var(--sidebar-border)',
              transform: 'translateY(-2px)',
            }
          : {},
      }}
    >
      <ProductLogo size={56} />
      <Typography variant="subtitle1" fontWeight={500} color="var(--text-primary)">
        {product.name}
      </Typography>
      {loading && <CircularProgress size={16} sx={{ color: 'var(--text-secondary)' }} />}
      {!enabled && (
        <Typography
          variant="caption"
          sx={{
            width: '100%',
            px: 1,
            textAlign: 'center',
            color: isNotAllowed ? '#dc2626' : 'var(--text-muted)',
          }}
        >
          {disabledCaption}
        </Typography>
      )}
    </Box>
  )
}

function ProductSelection() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const { setLang } = useLanguage()

  // Fire-and-forget cache warmup — runs in parallel with the /users/me
  // call so the navigation gate is not delayed. Resolves silently on
  // failure; the LanguageProvider falls back to its default in that case.
  const warmGlobalSettingsCache = () => {
    getGlobalSettings()
      .then((data) => {
        if (!data) return
        globalSettingsStorage.setCached({
          defaultLanguage: data.defaultLanguage,
          defaultTimezone: data.defaultTimezone,
          temperatureUnit: data.temperatureUnit,
        })
        if (data.defaultLanguage) setLang(data.defaultLanguage)
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[ProductSelection] /global-settings prefetch failed', err)
      })
  }

  const productsQuery = useQuery({
    queryKey: ['products', 'list'],
    queryFn: listProductsApi,
  })

  // Per-user allow-list persisted by the login flow. Read synchronously
  // from localStorage so gating works on a hard-refresh too. Empty array
  // means "no allow-list known" → fall back to status-only gating.
  const allowedSlugs = useMemo(() => getAllowedPlatformSlugs(), [])

  // Remote-login session (super-admin handshake) is Guardian-only — every
  // other product card stays disabled so the bearer is never handed to
  // another product portal.
  const isRemoteSession = remoteLogin.isActive()

  // Combine the static Omnia card with the live API list. We keep Omnia first
  // so its "Coming soon" slot is visually stable across reloads.
  const products = useMemo(() => {
    const apiProducts = (productsQuery.data || []).map((p) => {
      const slug = (p.slug || '').toUpperCase()
      const statusEnabled = (p.status || 'ACTIVE').toUpperCase() === 'ACTIVE'
      // No allow-list (empty array) keeps backwards-compatible behavior;
      // non-empty allow-list requires an exact slug match.
      const type = getProductType(p.slug)
      const isGuardian = type === 'guardian'
      // A remote session ignores the stored allow-list — it may belong to a
      // different user — and only Guardian stays selectable.
      const isAllowed = isRemoteSession
        ? isGuardian
        : allowedSlugs.length === 0 || allowedSlugs.includes(slug)
      return {
        id: `api-${p.id}`,
        name: p.name,
        slug,
        enabled: statusEnabled && isAllowed,
        disabledReason: !statusEnabled ? 'comingSoon' : !isAllowed ? 'notAllowed' : null,
        type,
      }
    })
    return [STATIC_OMNIA, ...apiProducts]
  }, [productsQuery.data, allowedSlugs, isRemoteSession])

  const handleSelect = async (product) => {
    // Belt-and-braces: the card's click/keyboard handlers already gate on
    // `enabled`, but if anything ever bypasses those, refuse to dispatch.
    if (!product.enabled) return
    if (product.type === 'move') {
      // Move portal handshake: forward the JWT from localStorage as a
      // `?token=…` query param and hard-redirect. No API call is made
      // on this path.
      const portalUrl = APIs.PORTALS.move
      if (!portalUrl) {
        // eslint-disable-next-line no-console
        console.error('Move portal URL is not configured (VITE_APP_MOVE_PORTAL_URL).')
        return
      }
      const token = tokenStorage.getAccessToken()
      const params = new URLSearchParams()
      if (token) params.set('token', token)
      const redirectPath = `${portalUrl.replace(/\/+$/, '')}/redirected`
      const target = params.toString() ? `${redirectPath}?${params.toString()}` : redirectPath
      window.location.href = target
      return
    }

    if (product.type === 'guardian') {
      setLoading(true)
      try {
        // One-time global-settings fetch — caches language / timezone /
        // temperature unit so the LanguageProvider and header can boot
        // without an extra round-trip. Runs alongside /users/me so the
        // onboarding gate stays snappy.
        warmGlobalSettingsCache()

        // Live call to GET /guardian-users/me. Flip back to
        // `STATIC_ME_RESPONSE` if the endpoint goes down again.
        const raw = await getGuardianMeApi()
        const userData = raw?.data || {}
        userStorage.setUserDetails(userData)

        // Sidebar / route permission gating relies on the /me payload
        // carrying a `permissions` array. While the backend rolls that
        // out, log a one-time warning so the rollout state is visible in
        // DevTools — the client still soft-allows access (see
        // useEffectivePermissions) so existing installs aren't locked out.
        if (userData && !Array.isArray(userData.permissions)) {
          // eslint-disable-next-line no-console
          console.warn(
            '[ProductSelection] /guardian-users/me did not return `permissions`. ' +
              'Sidebar module gating will fall back to allow-all until the backend starts ' +
              'shipping permissions on /me.'
          )
        }

        // Post-login onboarding gate:
        //   1. Missing `name` → user must fill in their profile details
        //      before anything else. Send them to /profile with a query
        //      flag that auto-opens the edit modal so the form is the
        //      first thing they see.
        //   2. isResetPassword === true → password still needs rotating.
        //      If they already have a name, take them through the
        //      launch chain; otherwise they were already routed to
        //      /profile in step 1.
        //   3. Everything checks out → drop them into the launch-widget
        //      screen.
        let nextRoute
        if (!userData?.isResetPassword) {
          nextRoute = ROUTES.RESET_PASSWORD
        } else if (!userData?.name?.trim()) {
          nextRoute = `${ROUTES.PROFILE}?openEdit=1`
        } else {
          nextRoute = ROUTES.BRANCH_SELECTION
        }
        navigate(nextRoute, { replace: true })
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[ProductSelection] /users/me failed', err)
        navigate(ROUTES.BRANCH_SELECTION, { replace: true })
      } finally {
        setLoading(false)
        // navigate(ROUTES.BRANCH_SELECTION, { replace: true })
      }
      return
    }
    navigate(ROUTES.BRANCH_SELECTION, { replace: true })
  }

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        bgcolor: 'var(--body-bg)',
        color: 'var(--text-primary)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <BackdropArc sx={{ width: 600, height: 600, top: -260, left: -260 }} />
      <BackdropArc sx={{ width: 600, height: 600, bottom: -300, right: -260 }} />

      {/* Centered brand + cards */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          pb: 10,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ProductLogo size={72} />
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography
              component="span"
              sx={{
                fontWeight: 700,
                fontSize: 44,
                color: '#dc2626',
                letterSpacing: 1,
                lineHeight: 1,
              }}
            >
              MdG
            </Typography>
            <Typography
              component="span"
              sx={{
                fontWeight: 500,
                fontSize: 44,
                color: 'var(--text-primary)',
                letterSpacing: 4,
                lineHeight: 1,
              }}
            >
              Ecosystem
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
          {products.map((p) => (
            <ProductCard
              key={p.id}
              product={p}
              loading={loading && (p.type === 'guardian' || p.type === 'move')}
              onSelect={handleSelect}
            />
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default ProductSelection
